<script setup lang="ts">
export type Props = {
  src?: string
  errorSrc?: string
  alt?: string
  width?: number | string
  height?: number | string
  cssWidth?: string
  cssHeight?: string
  minWidth?: string
  minHeight?: string
  maxWidth?: string
  maxHeight?: string
  rounded?: boolean
  aspectRatio?: string
  objectFit?: string
  position?: string
  hasLoading?: boolean
  lazyLoading?: boolean
  isHoverEffect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  alt: '',
  errorSrc: '/images/noimage.svg',
  width: '100%',
  height: '100%',
  cssWidth: 'auto',
  cssHeight: 'auto',
  minWidth: 'auto',
  minHeight: 'auto',
  maxWidth: 'none',
  maxHeight: 'none',
  rounded: false,
  aspectRatio: 'auto',
  objectFit: 'none',
  position: 'center',
  hasLoading: true,
  lazyLoading: true,
  isHoverEffect: false,
})

const root: Ref<HTMLImageElement | null> = ref(null)
const loaded = ref(false)
const loading = computed((): boolean => {
  return props.hasLoading && !loaded.value
})
const classObject = computed((): { [key: string]: boolean } => {
  return {
    'rounded': props.rounded,
    'loading': loading.value,
    'is-hoverEffect': props.isHoverEffect,
  }
})

const styleObject = computed((): { [key: string]: string | undefined } => {
  return {
    // スケルトンのサイズを補助する（ただし実際のサイズとは異なる）
    paddingBottom: loading.value && !props.width ? '100%' : undefined,
    width: props.cssWidth === 'auto' ? undefined : props.cssWidth,
    height: props.cssHeight === 'auto'
      ? props.objectFit !== 'none'
        ? '100%'
        : undefined
      : props.cssHeight,
    minWidth: props.minWidth === 'auto' ? undefined : props.minWidth,
    minHeight: props.minHeight === 'auto' ? undefined : props.minHeight,
    maxWidth: props.maxWidth === 'none' ? undefined : props.maxWidth,
    maxHeight: props.maxHeight === 'none' ? undefined : props.maxHeight,
    aspectRatio: props.aspectRatio === 'auto' ? undefined : props.aspectRatio,
    objectFit: props.objectFit === 'fill' ? undefined : props.objectFit,
    objectPosition: props.position,
  }
})

onMounted(() => {
  const rootElem = root.value

  if (rootElem) {
    rootElem.addEventListener(
      'error',
      () => {
        loaded.value = true
        rootElem.src = props.errorSrc
      },
    )

    if (rootElem.complete) {
      loaded.value = true
      return
    } else {
      rootElem.addEventListener(
        'load',
        () => {
          loaded.value = true
        },
      )
    }
  }
})
</script>

<template>
  <div
    class="extended-image"
    v-bind:class="classObject"
  >
    <img
      ref="root"
      v-bind:src="src"
      v-bind:alt="alt"
      v-bind:width="width"
      v-bind:height="height"
      v-bind:style="styleObject"
      v-bind:loading="lazyLoading ? 'lazy' : 'eager'"
      v-bind:data-src="src"
    >
  </div>
</template>

<style lang="scss" scoped>
.extended-image {
  display: block;
  max-width: none;
  height: 100%;
  overflow: hidden;
  backface-visibility: hidden;

  img {
    display: block;
  }

  &.loading {
    animation: skeleton-loading 1.3s linear infinite;

    /* prettier-ignore */
    background-image:
      linear-gradient(
        to right,
        #f8f8f8 0%,
        #ddd 50%,
        #ddd 55%,
        #f8f8f8 100%
      );
    background-repeat: repeat;

    /* prettier-ignore */
    background-size: 200%;
  }

  &.rounded {
    border-radius: 0.5rem;
  }

  &.is-hoverEffect > img {
    transition: transform 1s, opacity 1s;

    &:hover {
      transform: scale(1.1);
      transition: transform 0.4s, opacity 1s;
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 0;
  }

  100% {
    background-position: 200% 0;
  }
}
</style>
